/**
 * Credits denial envelope (ADR 7 / C-3.7).
 *
 * C-4's shared frontend metering module extends this shape; it does not replace it.
 * A 403 is a denial unless `reason === SESSION_EXPIRED`. Status alone never
 * means the session is gone.
 */

export const DENIAL_REASONS = [
    'NO_CLAIM',
    'INSUFFICIENT_CREDITS',
    'ACCOUNT_LOCKED',
    'SESSION_EXPIRED',
] as const;

export type DenialReason = (typeof DENIAL_REASONS)[number];

export const SESSION_EXPIRED_MESSAGE = 'Session expired. Please sign in again.';

export interface DenialEnvelope {
    statusCode?: number;
    error?: string;
    code?: string;
    message?: string;
    reason?: string;
    functionKey?: string | null;
}

export class ApiDenialError extends Error {
    readonly statusCode: number;
    readonly code: string | undefined;
    readonly reason: DenialReason | undefined;
    readonly functionKey: string | null;
    readonly envelope: DenialEnvelope;

    constructor(envelope: DenialEnvelope, fallbackMessage: string) {
        super(envelope.message || fallbackMessage);
        this.name = 'ApiDenialError';
        this.statusCode = envelope.statusCode ?? 403;
        this.code = envelope.code;
        this.reason = isDenialReason(envelope.reason) ? envelope.reason : undefined;
        this.functionKey = envelope.functionKey ?? null;
        this.envelope = envelope;
    }
}

export function isDenialReason(value: unknown): value is DenialReason {
    return typeof value === 'string' && (DENIAL_REASONS as readonly string[]).includes(value);
}

export function isSessionExpiredDenial(envelope: DenialEnvelope | undefined): boolean {
    return envelope?.reason === 'SESSION_EXPIRED';
}

export function parseDenialEnvelope(data: unknown): DenialEnvelope {
    if (!data || typeof data !== 'object') {
        return {};
    }
    const body = data as Record<string, unknown>;
    return {
        statusCode: typeof body.statusCode === 'number' ? body.statusCode : undefined,
        error: typeof body.error === 'string' ? body.error : undefined,
        code: typeof body.code === 'string' ? body.code : undefined,
        message: typeof body.message === 'string' ? body.message : undefined,
        reason: typeof body.reason === 'string' ? body.reason : undefined,
        functionKey: typeof body.functionKey === 'string' ? body.functionKey : null,
    };
}

export interface ForbiddenNavigateTarget {
    href: string;
}

/**
 * Decide what a 403 does. Only `reason: SESSION_EXPIRED` is a session
 * termination. Every other 403 — including a body with no `reason` — is a
 * typed denial. The caller supplies `navigate` so a production build can be
 * asserted without touching `window.location`.
 */
export function resolveForbidden403(
    data: unknown,
    options: {
        isProduction: boolean;
        canNavigate: boolean;
        signInHref: (callbackUrl: string) => string;
        currentUrl: string;
        navigate?: (target: ForbiddenNavigateTarget) => void;
    },
): ApiDenialError | Error {
    const envelope = parseDenialEnvelope(data);

    if (isSessionExpiredDenial(envelope)) {
        if (options.isProduction && options.canNavigate && options.navigate) {
            options.navigate({ href: options.signInHref(options.currentUrl) });
        }
        return new Error(SESSION_EXPIRED_MESSAGE);
    }

    return new ApiDenialError(envelope, 'Forbidden');
}
